import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconEdit, IconPlus } from '../../../common/icons';
import { useRacesList } from '../contexts/races';
import SectionTabs from '../../navigation/section-tabs';
import { ANIMAL_TABS } from '../../navigation/tabs';

import '../../../common/styles/catalog.css';

const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };

export default function RacesOverview() {
    const [params, setParams] = useSearchParams();
    const urlSearch = params.get('q') || '';

    const [search, setSearch] = useState(urlSearch);
    const [debounced, setDebounced] = useState(urlSearch);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debounced) next.set('q', debounced); else next.delete('q');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const filters = useMemo(() => (debounced ? { q: debounced } : {}), [debounced]);
    const { data, isLoading, isFetching, error } = useRacesList(filters);
    const races = data?.items ?? [];
    const total = data?.pagination?.total ?? races.length;

    return (
        <div className="cat-overview">
            <SectionTabs tabs={ANIMAL_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Razas</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="races" action="write">
                    <Link to="/admin/races/new" className="cat-overview__add">
                        <IconPlus />
                        <span>Nueva raza</span>
                    </Link>
                </Gate>
            </header>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por nombre o código…"
                        aria-label="Buscar razas por nombre o código"
                    />
                </div>
            </div>

            {error && <div className="cat-overview__error">{error.message || 'No se pudieron cargar las razas'}</div>}
            {total > races.length && <p className="cat-overview__note">Mostrando las primeras {races.length} de {total} razas. Refiná la búsqueda.</p>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando razas…</div>
            ) : !races.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">
                        {debounced ? 'Ninguna raza coincide con la búsqueda.' : 'Aún no hay razas.'}
                    </p>
                    {!debounced && (
                        <Gate domain="races" action="write">
                            <Link to="/admin/races/new" className="cat-overview__add">
                                <IconPlus /><span>Crear la primera raza</span>
                            </Link>
                        </Gate>
                    )}
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Código</th>
                                <th>Estado</th>
                                <th className="cat-table__actions-col" aria-label="Acciones" />
                            </tr>
                        </thead>
                        <tbody>
                            {races.map((r) => (
                                <tr key={r.id} className="cat-table__row">
                                    <td>
                                        <Link to={`/admin/races/${r.id}`} className="cat-table__name">{r.name}</Link>
                                    </td>
                                    <td>{r.code || <span className="cat-table__empty">—</span>}</td>
                                    <td><span className={`cat-badge cat-badge--${r.status}`}>{STATUS_LABEL[r.status] || '—'}</span></td>
                                    <td className="cat-table__actions">
                                        <Gate domain="races" action="write">
                                            <Link to={`/admin/races/${r.id}/edit`} className="cat-table__edit" aria-label={`Editar ${r.name}`} title="Editar">
                                                <IconEdit />
                                            </Link>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
